import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { categoryService, monthlyPlanService, recurringExpenseService } from '../../../apiServices';
import {
  Category,
  MonthlyPlan,
  MonthlyPlanFixedExpense,
  MonthlyPlanOverview,
  MonthlyPlanTemplate
} from '../../../apiServices/types';
import { useNotification } from '../../../contexts/NotificationContext';
import { useRefresh } from '../../../contexts/RefreshContext';
import {
  buildPlanInput,
  computeMonthlyPlan,
  formatDateKey,
  getCurrentPeriod,
  getDaysInMonth,
  isSamePeriod,
  makeDateKey,
  MONTH_NAMES,
  parsePeriodKey,
  PlanPeriod,
  shiftPeriod,
  toLocalDateString,
  toLocalISOString,
  toPeriodKey,
  toUTCDateString
} from '../../../utils';
import { ConfirmationModal } from '../../UI';
import { MonthlyPlanDashboard, MonthlyPlanEditor, MonthSwitcher } from './components';
import '../BudgetsPage/BudgetsPage.css';
import '../RecurringPage/RecurringPage.css';
import '../../BudgetTemplateModal/BudgetTemplateModal.css';
import './MonthlyPlanPage.css';

interface EditorData {
  template: MonthlyPlanTemplate;
  categories: Category[];
}

const MonthlyPlanPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get('month');
  const period = useMemo<PlanPeriod>(() => parsePeriodKey(monthParam) ?? getCurrentPeriod(), [monthParam]);
  const [overview, setOverview] = useState<MonthlyPlanOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editorData, setEditorData] = useState<EditorData | null>(null);
  const [isOpeningEditor, setIsOpeningEditor] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemToPay, setItemToPay] = useState<MonthlyPlanFixedExpense | null>(null);
  const [payingItemId, setPayingItemId] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadOverview = useCallback(async (target: PlanPeriod, showLoader: boolean): Promise<void> => {
    // Ignore responses of months the user has already switched away from
    const requestId = ++requestIdRef.current;
    if (showLoader) {
      setLoading(true);
    }

    try {
      const data = await monthlyPlanService.getOverview(target.year, target.month);
      if (requestId === requestIdRef.current) {
        setOverview(data);
        setLoadError('');
      }
    } catch (error: any) {
      if (requestId === requestIdRef.current) {
        setLoadError(error.message || 'Помилка завантаження плану');
        showNotification('Помилка завантаження плану місяця', 'error');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [showNotification]);

  useEffect(() => {
    setEditorData(null);
    setOverview(null);
    loadOverview(period, true);
  }, [period, loadOverview]);

  const handleRefresh = useCallback(async () => {
    if (!editorData) {
      await loadOverview(period, false);
    }
  }, [editorData, loadOverview, period]);

  useRefresh(handleRefresh);

  const todayKey = toLocalDateString(new Date());
  const isCurrent = isSamePeriod(period, getCurrentPeriod());
  const plan = overview?.plan ?? null;

  const summary = useMemo(
    () => (overview?.plan ? computeMonthlyPlan(buildPlanInput(overview.plan), overview.activity, todayKey) : null),
    [overview, todayKey]
  );

  const changePeriod = (next: PlanPeriod): void => {
    if (isSamePeriod(next, getCurrentPeriod())) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ month: toPeriodKey(next) }, { replace: true });
    }
  };

  const openEditor = async (): Promise<void> => {
    setIsOpeningEditor(true);
    try {
      const [template, categories] = await Promise.all([
        monthlyPlanService.getTemplate(period.year, period.month),
        categoryService.getAll()
      ]);
      setEditorData({ template, categories });
    } catch (error: any) {
      showNotification(error.message || 'Помилка завантаження даних для плану', 'error');
    } finally {
      setIsOpeningEditor(false);
    }
  };

  const handleSaved = async (savedPlan: MonthlyPlan): Promise<void> => {
    showNotification(plan ? 'План місяця оновлено' : 'План місяця створено', 'success');
    setEditorData(null);
    setOverview(prev => (prev ? { ...prev, plan: savedPlan } : prev));
    await loadOverview(period, false);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!plan) return;

    setIsDeleting(true);
    try {
      await monthlyPlanService.delete(plan.id);
      showNotification('План місяця видалено', 'success');
      await loadOverview(period, false);
    } catch (error: any) {
      showNotification(error.message || 'Помилка видалення плану', 'error');
    } finally {
      setIsDeleting(false);
      setShowConfirmDelete(false);
    }
  };

  // The payment has to be dated inside the plan month to be recognized in it
  const getPaymentDateKey = (item: MonthlyPlanFixedExpense): string => {
    if (isCurrent) {
      return todayKey;
    }

    const day = Math.min(item.dayOfMonth ?? 1, getDaysInMonth(period.year, period.month));
    return makeDateKey(period.year, period.month, day);
  };

  const handleConfirmPay = async (): Promise<void> => {
    const item = itemToPay;
    setItemToPay(null);

    if (!item?.recurringExpenseId) return;

    setPayingItemId(item.id);
    try {
      const date = isCurrent ? toLocalISOString(new Date()) : toUTCDateString(getPaymentDateKey(item), '12:00:00');
      await recurringExpenseService.createReceipt(item.recurringExpenseId, date);
      showNotification(`Витрату «${item.name}» створено`, 'success');
      await loadOverview(period, false);
    } catch (error: any) {
      showNotification(error.message || 'Помилка створення витрати з шаблону', 'error');
    } finally {
      setPayingItemId(null);
    }
  };

  const periodLabel = `${MONTH_NAMES[period.month - 1].toLowerCase()} ${period.year}`;

  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <div className="card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження плану...</p>
          </div>
        </div>
      );
    }

    if (editorData && overview) {
      return (
        <MonthlyPlanEditor
          key={toPeriodKey(period)}
          period={period}
          template={editorData.template}
          categories={editorData.categories}
          plan={plan}
          activity={overview.activity}
          onCancel={() => setEditorData(null)}
          onSaved={handleSaved}
        />
      );
    }

    if (plan && summary) {
      return (
        <MonthlyPlanDashboard
          period={period}
          plan={plan}
          summary={summary}
          payingItemId={payingItemId}
          onPay={setItemToPay}
        />
      );
    }

    if (loadError && !overview) {
      return (
        <div className="empty-state card">
          <div className="empty-title">Не вдалося завантажити план</div>
          <div className="empty-text">{loadError}</div>
          <button type="button" className="btn btn-primary monthly-plan-empty-btn" onClick={() => loadOverview(period, true)}>
            Спробувати ще раз
          </button>
        </div>
      );
    }

    return (
      <div className="empty-state card">
        <div className="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>
        <div className="empty-title">Плану на {periodLabel} ще немає</div>
        <div className="empty-text">
          Вкажіть дохід, сталі витрати та заощадження — система розподілить вільні кошти по тижнях
          і відстежуватиме витрати автоматично
        </div>
        <button
          type="button"
          className="btn btn-primary monthly-plan-empty-btn"
          onClick={openEditor}
          disabled={isOpeningEditor}
        >
          {isOpeningEditor ? 'Завантаження...' : 'Створити план'}
        </button>
      </div>
    );
  };

  return (
    <div className="monthly-plan-page">
      <div className="budgets-page-header">
        <div className="budgets-page-header-content">
          <h2 className="budgets-page-header-title">План місяця</h2>
          <p className="budgets-page-header-subtitle">
            {editorData
              ? (plan ? `Редагування плану на ${periodLabel}` : `Новий план на ${periodLabel}`)
              : 'Розподіліть дохід і контролюйте вільні кошти по тижнях'}
          </p>
        </div>
        {plan && !editorData && !loading && (
          <div className="budgets-page-header-actions">
            <button className="budgets-page-header-button" onClick={openEditor} disabled={isOpeningEditor}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              <span>{isOpeningEditor ? 'Завантаження...' : 'Редагувати'}</span>
            </button>
            <button className="budgets-page-header-button" onClick={() => setShowConfirmDelete(true)} disabled={isDeleting}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3,6 5,6 21,6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              <span>Видалити</span>
            </button>
          </div>
        )}
      </div>

      <MonthSwitcher
        period={period}
        isCurrent={isCurrent}
        disabled={!!editorData || isOpeningEditor}
        onPrevious={() => changePeriod(shiftPeriod(period, -1))}
        onNext={() => changePeriod(shiftPeriod(period, 1))}
        onToday={() => changePeriod(getCurrentPeriod())}
      />

      {renderContent()}

      <ConfirmationModal
        isOpen={showConfirmDelete}
        title="Видалити план?"
        message={`План на ${periodLabel} буде видалено. Витрати, перекази та шаблони залишаться без змін.`}
        confirmText="Видалити"
        cancelText="Скасувати"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        variant="danger"
        isLoading={isDeleting}
      />

      <ConfirmationModal
        isOpen={!!itemToPay}
        title="Сплатити з шаблону?"
        message={itemToPay
          ? `Буде створено витрату за шаблоном «${itemToPay.name}» від ${formatDateKey(getPaymentDateKey(itemToPay))} — вона одразу зарахується як платіж у плані`
          : ''}
        confirmText="Сплатити"
        cancelText="Скасувати"
        onConfirm={handleConfirmPay}
        onCancel={() => setItemToPay(null)}
      />
    </div>
  );
};

export default MonthlyPlanPage;
